import { Component, ElementRef, ViewChild, ViewEncapsulation, type OnDestroy, type OnInit } from '@angular/core';
import { FormGroup, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';

import { Store } from '@ngrx/store';
import { Subscription, type Observable } from 'rxjs';
import { take } from 'rxjs/operators';

import { LoaderType } from '@fcom/common/';
import { GtmService } from '@fcom/common/gtm';
import { ScrollService } from '@fcom/common/services';
import { ConfigService } from '@fcom/core';
import type { Profile } from '@fcom/core-api/login';
import type { AppState } from '@fcom/core/interfaces';
import { profileOrUndefinedIfNotLoggedIn } from '@fcom/core/selectors';
import { NAME_PATTERN, unsubscribe } from '@fcom/core/utils';
import { FormStatus } from '@fcom/service-forms/interfaces';
import { trackFormState } from '@fcom/service-forms/services';
import {
  ATTACHMENT_ALLOWED_FILE_EXTENSIONS,
  ATTACHMENT_MAX_SIZE_IN_BYTES,
  ButtonType,
  NotificationLayout,
  NotificationTheme,
} from '@fcom/ui-components';
import { FileUploaderComponent } from '@fcom/ui-components/components/forms/file-uploader/file-uploader.component';
import type { Attachment } from '@fcom/ui-components/components/interfaces';

import { FileOrigin, RetrieveFields, type CaseAttachmentFormGroup, type RetrieveCase } from '../interfaces';
import { ClaimOrFeedbackService } from '../services/claim-or-feedback.service';
import { CASE_NUMBER_PATTERN } from '../utils';

export const REQUEST_CASE_ATTACHMENT = 'request-case-attachment';

@Component({
  selector: 'fin-case-attachments-form',
  templateUrl: 'case-attachments-form.component.html',
  styleUrls: ['./case-attachments-form.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class CaseAttachmentsFormComponent implements OnInit, OnDestroy {
  readonly allowedFileTypes: FileUploaderComponent['allowedFileTypes'] = ATTACHMENT_ALLOWED_FILE_EXTENSIONS;
  readonly maxAttachments: FileUploaderComponent['maxAttachments'] = 1;
  readonly maxSizeInBytes: FileUploaderComponent['maxSizeInBytes'] = ATTACHMENT_MAX_SIZE_IN_BYTES;

  readonly ButtonType = ButtonType;
  readonly FormStatus = FormStatus;
  readonly LoaderType = LoaderType;
  readonly NotificationLayout = NotificationLayout;
  readonly NotificationTheme = NotificationTheme;

  private subscriptions: Subscription = new Subscription();

  reactiveForm: FormGroup<CaseAttachmentFormGroup>;
  formStatus: FormStatus = FormStatus.INITIAL;

  @ViewChild('fileUploader') fileUploader: FileUploaderComponent;
  @ViewChild('formContainer', { static: true }) formContainer: ElementRef;

  constructor(
    private store$: Store<AppState>,
    private fb: UntypedFormBuilder,
    private scrollService: ScrollService,
    private claimAndFeedbackService: ClaimOrFeedbackService,
    private gtmService: GtmService,
    private configService: ConfigService
  ) {}

  get attachment(): Attachment | undefined {
    return this.fileUploader?.attachments[0];
  }

  ngOnInit(): void {
    this.formStatus = FormStatus.INITIAL;
    this.subscriptions.add(
      this.store$.pipe(profileOrUndefinedIfNotLoggedIn(), take(1)).subscribe((profile: Profile): void => {
        this.createForm(profile);
      })
    );
  }

  ngOnDestroy(): void {
    this.subscriptions = unsubscribe(this.subscriptions);
  }

  createForm(profile?: Profile): void {
    this.reactiveForm = this.fb.group({
      lastName: [
        profile?.lastname ?? '',
        [Validators.required, Validators.pattern(NAME_PATTERN), Validators.maxLength(50)],
      ],
      caseNumber: ['', [Validators.pattern(CASE_NUMBER_PATTERN), Validators.required]],
      file: [[], Validators.required],
    });
  }

  onSubmit(): void {
    if (!this.reactiveForm.valid) {
      this.reactiveForm.markAllAsTouched();
      this.reactiveForm.updateValueAndValidity();

      return;
    }

    this.formStatus = FormStatus.LOADING;
    this.scrollService.scrollToImmediate(this.formContainer);
    trackFormState(REQUEST_CASE_ATTACHMENT, 'send-attempt', this.gtmService);
    this.submitToSalesforce();
  }

  private submitToSalesforce(): void {
    const file = this.fileUploader?.attachments[0]?.file;
    const caseId = this.reactiveForm.value?.caseNumber;
    const lastName = this.reactiveForm.value?.lastName;
    if (this.configService.cfg.enableNewUploadAttachmentFlow) {
      this.uploadFile(file, FileOrigin.CASE, undefined, caseId, lastName);
    } else {
      this.subscriptions.add(
        this.fetchCase(caseId, lastName).subscribe({
          next: (res) => {
            if (!res) {
              this.formStatus = FormStatus.ERROR;
              trackFormState(REQUEST_CASE_ATTACHMENT, 'error', this.gtmService);
              return;
            }
            this.uploadFile(file, FileOrigin.CASE, res.caseSFId);
          },
          error: () => {
            this.formStatus = FormStatus.ERROR;
            trackFormState(REQUEST_CASE_ATTACHMENT, 'error', this.gtmService);
          },
        })
      );
    }
  }

  fetchCase(caseId: string, lastName: string): Observable<RetrieveCase> {
    const group = this.fb.group({
      [RetrieveFields.FAMILY_NAME]: lastName,
      [RetrieveFields.CASE_NUMBER]: caseId,
    });
    const form: UntypedFormGroup = new UntypedFormGroup({});
    form.setControl(RetrieveFields.RETRIEVE_FORM, group);
    return this.claimAndFeedbackService.fetchCase(form.getRawValue()).pipe(take(1));
  }

  uploadFile(file: File, origin: FileOrigin, salesforceCaseId?: string, caseId?: string, lastName?: string): void {
    this.subscriptions.add(
      this.claimAndFeedbackService
        .uploadFile<{ results: string }>(file, origin, salesforceCaseId, caseId, lastName)
        .pipe(take(1))
        .subscribe({
          next: () => {
            trackFormState(REQUEST_CASE_ATTACHMENT, 'success', this.gtmService);
            this.formStatus = FormStatus.COMPLETE;
          },
          error: () => {
            this.formStatus = FormStatus.ERROR;
            trackFormState(REQUEST_CASE_ATTACHMENT, 'error', this.gtmService);
          },
        })
    );
  }
}
